import { GOOGLE_ADS_CONSENT_COOKIE, GOOGLE_ADS_ID, GOOGLE_ADS_SIGNUP_DESTINATION } from './google-ads-config';

type Tag = (...args: unknown[]) => void;
type AdsWindow = Window & { dataLayer?: unknown[]; gtag?: Tag };
let loading: Promise<void> | undefined;
const sent = new Set<string>();

export function hasGoogleAdsConsent(): boolean {
    return document.cookie.split('; ').includes(`${GOOGLE_ADS_CONSENT_COOKIE}=granted`);
}

export function safeAdsLocation(): string {
    const source = new URL(window.location.href);
    const safe = new URL(source.origin + source.pathname);
    for (const name of ['gclid', 'gbraid', 'wbraid']) {
        const value = source.searchParams.get(name);
        if (value && /^[\w-]{1,512}$/.test(value)) safe.searchParams.set(name, value);
    }
    return safe.href;
}

export function adsTag(...args: unknown[]) {
    (window as AdsWindow).gtag?.(...args);
}

export function loadGoogleAds(): Promise<void> {
    if (!hasGoogleAdsConsent()) return Promise.reject(new Error('Consent required'));
    if (loading) {
        adsTag('consent', 'update', {
            ad_storage: 'granted', ad_user_data: 'granted',
            ad_personalization: 'denied', analytics_storage: 'denied',
        });
        return loading;
    }
    const browser = window as AdsWindow;
    browser.dataLayer ??= [];
    if (!browser.gtag) {
        // Keep Google's vendor bootstrap literal: gtag requires an Arguments
        // command, not a rest-parameter Array (the live endpoint rejects those).
        // Static trusted code only; no user/config values are interpolated.
        const bootstrap = document.createElement('script');
        bootstrap.textContent = 'window.gtag = function(){window.dataLayer.push(arguments);};';
        document.head.appendChild(bootstrap);
        bootstrap.remove();
    }
    if (!browser.gtag) return Promise.reject(new Error('Google tag bootstrap blocked'));
    adsTag('consent', 'default', {
        ad_storage: 'granted', ad_user_data: 'granted',
        ad_personalization: 'denied', analytics_storage: 'denied',
    });
    adsTag('js', new Date());
    // Explicit empty data overrides automatic enhanced-conversion collection.
    adsTag('set', 'user_data', {});
    adsTag('config', GOOGLE_ADS_ID, {
        user_data: {},
        send_page_view: false, allow_ad_personalization_signals: false,
        allow_enhanced_conversions: false, page_location: safeAdsLocation(), page_referrer: '',
    });
    loading = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => { loading = undefined; script.remove(); reject(new Error('Google tag unavailable')); };
        document.head.appendChild(script);
    });
    return loading;
}

export function sendGoogleAdsSignup(transactionId: string): boolean {
    if (!hasGoogleAdsConsent() || sent.has(transactionId)) return false;
    const key = `iloris_ads_sent:${transactionId}`;
    try { if (window.localStorage.getItem(key)) return false; } catch { /* Google also deduplicates by transaction ID. */ }
    sent.add(transactionId);
    adsTag('event', 'conversion', {
        send_to: GOOGLE_ADS_SIGNUP_DESTINATION, transaction_id: transactionId,
        user_data: {},
        page_location: safeAdsLocation(), page_referrer: '',
        event_callback: () => {
            // This is tag processing, not proof that Ads attributed a signup.
            if (!hasGoogleAdsConsent()) return;
            try { window.localStorage.setItem(key, 'sent'); } catch { /* Storage may be disabled. */ }
        },
    });
    return true;
}

export function denyGoogleAds() {
    // Stop in-flight conversion callbacks before persisting the choice.
    if (hasGoogleAdsConsent()) document.cookie = `${GOOGLE_ADS_CONSENT_COOKIE}=denied; Path=/; SameSite=Lax`;
    adsTag('consent', 'update', {
        ad_storage: 'denied', ad_user_data: 'denied',
        ad_personalization: 'denied', analytics_storage: 'denied',
    });
}
